import { useEffect, useCallback } from 'react';
import { useSuspenseQuery, useQuery } from '@apollo/client';
import {
  QUERY_FETCH_PODCAST_LIST_PAGE,
  QUERY_FETCH_PODCASTS,
  PodcastListPageQuery,
  FetchPodcastsQuery,
  FetchPodcastsQueryVariables,
  Podcast,
  PodcastListPage,
} from '@edwin-group/people-graphql';
import { usePodcastListStore } from '../store/usePodcastListStore';
import { getAuthentication } from '@/hooks/useAuthentication';

const PAGE_SIZE = 6;

export const usePodcastList = () => {
  const { data: pageData } = useSuspenseQuery<PodcastListPageQuery>(QUERY_FETCH_PODCAST_LIST_PAGE);

  const pagination = usePodcastListStore((state) => state.pagination);
  const setPage = usePodcastListStore((state) => state.setPage);
  const setPodcasts = usePodcastListStore((state) => state.setPodcasts);
  const appendPodcasts = usePodcastListStore((state) => state.appendPodcasts);
  const setPagination = usePodcastListStore((state) => state.setPagination);
  const setLoading = usePodcastListStore((state) => state.setLoading);

  const userDocumentId = getAuthentication()?.user?.documentId || '';

  const {
    data: podcastsData,
    fetchMore,
    loading,
  } = useQuery<FetchPodcastsQuery, FetchPodcastsQueryVariables>(QUERY_FETCH_PODCASTS, {
    variables: {
      page: 1,
      pageSize: PAGE_SIZE,
      userDocumentId,
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (pageData?.podcastListPage) {
      setPage(pageData.podcastListPage as PodcastListPage);
    }
  }, [pageData, setPage]);

  useEffect(() => {
    if (podcastsData?.podcasts) {
      const nodes = podcastsData.podcasts.nodes || [];
      const pageInfo = podcastsData.podcasts.pageInfo;
      const filteredNodes = nodes.filter((node): node is NonNullable<typeof node> => node !== null);
      setPodcasts(filteredNodes as Podcast[]);
      if (pageInfo) {
        setPagination({
          page: pageInfo.page || 1,
          pageCount: pageInfo.pageCount || 1,
          total: pageInfo.total || 0,
          pageSize: pageInfo.pageSize || PAGE_SIZE,
        });
      }
    }
  }, [podcastsData, setPodcasts, setPagination]);

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  const loadMore = useCallback(async () => {
    const nextPage = pagination.page + 1;
    if (nextPage > pagination.pageCount) return;

    setLoading(true);
    const { data } = await fetchMore({
      variables: {
        page: nextPage,
        pageSize: PAGE_SIZE,
        userDocumentId,
      },
    });

    if (data?.podcasts) {
      const nodes = data.podcasts.nodes || [];
      const pageInfo = data.podcasts.pageInfo;
      const filteredNodes = nodes.filter((node): node is NonNullable<typeof node> => node !== null);
      appendPodcasts(filteredNodes as Podcast[]);
      if (pageInfo) {
        setPagination({
          page: pageInfo.page || nextPage,
          pageCount: pageInfo.pageCount || pagination.pageCount,
          total: pageInfo.total || pagination.total,
          pageSize: pageInfo.pageSize || PAGE_SIZE,
        });
      }
    }
    setLoading(false);
  }, [pagination, fetchMore, userDocumentId, appendPodcasts, setPagination, setLoading]);

  const hasMore = pagination.page < pagination.pageCount;

  return { loading, loadMore, hasMore };
};
